import { AudienceWithCompletionCriteria } from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import {
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { cloneDeep, compact, isEqual, partition, sortBy } from 'lodash';
import { Archive, Edit, Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import shortid from 'shortid';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../components/AdditionalActionsMenu';
import { WithShape } from '../../../../../components/Charts/Util';
import { ColorPicker } from '../../../../../components/ColorPicker';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { Loader } from '../../../../../components/Loader';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useRoutes } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  getAudienceName,
  getRandomAudienceColor,
  getRandomAudienceColorExceptPreviousColor
} from '../../../../../services/experiences/audience';
import { LightMdToJsx } from '../../../../../services/formatter';
import { isFreeTierOrg } from '../../../../../services/organizationTier';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import {
  useUserRuleConfigWithAutocompletion,
  useUsersByOrgId
} from '../../../../../services/user';
import { useTheme } from '../../../../../themes';
import {
  AudienceBlockContainer,
  AudienceBlockTitle,
  AudienceMatchDialog,
  DEFAULT_AUDIENCE_COLOR
} from '../../../../Experiences/components/Builders/TaskBuilder/components/Audiences';
import {
  AddButton,
  EMPTY_RULE_GROUP,
  isEmptyRule,
  RuleBuilder
} from '../../../../Experiences/components/RuleBuilder';
import { StandardDrawer } from '../../../../Experiences/components/StandardDrawer';
import {
  getBlocksForMomentAudience,
  getEmptyAudience,
  getEveryoneAudience
} from '../../../services';
import { EditComponentProps, ViewComponentProps } from '../types';

const AudiencesGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: p.theme.spacing()
}));

const AudienceActions = styled('div')((p) => ({
  display: 'flex',
  gap: p.theme.spacing()
}));

const AudienceCard = ({
  audience,
  hasOtherAudiences,
  onDuplicateAudience,
  onRestoreAudience,
  onDeleteAudience,
  setDrawerAudience
}: {
  audience: AudienceWithCompletionCriteria;
  hasOtherAudiences: boolean;
  onDeleteAudience: (audience: AudienceWithCompletionCriteria) => void;
  onRestoreAudience: (audience: AudienceWithCompletionCriteria) => void;
  onDuplicateAudience: (audience: AudienceWithCompletionCriteria) => void;
  setDrawerAudience: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return compact([
      {
        key: 'duplicate',
        label: 'Duplicate',
        onClick: () => {
          onDuplicateAudience(audience);
        }
      },
      !audience.inactive && {
        key: 'remove',
        label: 'Remove',
        onClick: () => {
          onDeleteAudience(audience);
        }
      },
      audience.inactive && {
        key: 'restore',
        label: 'Restore',
        onClick: () => {
          onRestoreAudience(audience);
        }
      }
    ]);
  }, [audience, onDeleteAudience, onDuplicateAudience, onRestoreAudience]);

  return (
    <AudienceBlockContainer
      role="button"
      selected={false}
      onClick={() => {
        setDrawerAudience();
      }}
    >
      <FlexContainer justifyContent="space-between" alignItems="center">
        <AudienceBlockTitle>
          <WithShape
            wrap
            shape="square"
            color={audience.color || DEFAULT_AUDIENCE_COLOR}
          >
            <Typography variant="h6">
              {getAudienceName(audience, hasOtherAudiences, t)}
            </Typography>
          </WithShape>
        </AudienceBlockTitle>
        <AudienceActions>
          {audience.rule === 'EVERYONE' ? (
            audience.inactive ? (
              <Tooltip title={`${t('Restore')}`}>
                <IconButton
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    onRestoreAudience(audience);
                  }}
                >
                  <RefreshCw size={20} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={`${t('Remove')}`}>
                <IconButton
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    onDeleteAudience(audience);
                  }}
                >
                  <Trash2 size={20} />
                </IconButton>
              </Tooltip>
            )
          ) : (
            <>
              <Tooltip title={`${t('Edit')}`}>
                <IconButton>
                  <Edit size={20} />
                </IconButton>
              </Tooltip>
              <AdditionalActionsMenu options={options} />
            </>
          )}
        </AudienceActions>
      </FlexContainer>
    </AudienceBlockContainer>
  );
};

export const AudienceDrawer = ({
  open,
  onClose,
  value,
  onChange,
  audience
}: {
  open: boolean;
  onClose: () => void;
  value: EditComponentProps['value'];
  onChange: EditComponentProps['onChange'];
  audience: AudienceWithCompletionCriteria;
}) => {
  const { t } = useUnsafeTranslation();
  const [errors, setErrors] = useState<Set<'NO_NAME' | 'NO_RULE'>>(new Set());
  const [
    editableAudience,
    setEditableAudience
  ] = useState<AudienceWithCompletionCriteria>(audience);
  const { trackEvent } = useProductAnalytics();
  const { org } = useCurrentUser();
  const previewDialog = useDialogState();
  const { customUserFields } = org;
  const originalAudiences = value.data.content.audiences;

  const config = useUserRuleConfigWithAutocompletion(
    value.data.orgId,
    customUserFields,
    value.data.tz,
    {
      orgId: org.id,
      experienceTemplateId: 'MOMENT' // just not null, which would ignore any custom rules inside
    }
  );

  const audienceName = getAudienceName(
    editableAudience,
    originalAudiences.length > 1,
    t,
    {
      noDefault: true
    }
  );

  return (
    <StandardDrawer onClose={onClose} open={open}>
      <FlexContainer
        direction="column"
        alignItems="flex-start"
        spacing={4}
        fullWidth
      >
        <Typography variant="h5">
          {isEqual(audience.rule, { ...EMPTY_RULE_GROUP })
            ? t('Create Target Audience')
            : t('Edit Target Audience')}
        </Typography>
        <TextField
          label={t('Audience Name')}
          placeholder={t('Enter a name')}
          value={audienceName}
          onChange={(ev) =>
            setEditableAudience((prev) => ({
              ...prev,
              name: ev.target.value
            }))
          }
          error={errors.has('NO_NAME')}
          helperText={errors.has('NO_NAME') && t('Please provide a name')}
          disabled={editableAudience.rule === 'EVERYONE'}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ColorPicker
                  size={24}
                  value={editableAudience.color || DEFAULT_AUDIENCE_COLOR}
                  onChange={(nextColor) =>
                    setEditableAudience((prev) => ({
                      ...prev,
                      color: nextColor
                    }))
                  }
                  disabled={editableAudience.rule === 'EVERYONE'}
                />
              </InputAdornment>
            )
          }}
        />

        {editableAudience.rule === 'EVERYONE' && (
          <LightMdToJsx>
            {t(
              originalAudiences.length > 1
                ? "Employees who don't match the rules for any other target audiences fall into this audience."
                : '**{{ name }}** is the default audience, which includes every Employee uploaded to Dado.',
              { name: audienceName }
            )}
          </LightMdToJsx>
        )}
        {editableAudience.rule !== 'EVERYONE' && (
          <>
            <CustomInputLabel fullWidth label={t('Target Audience Rules')}>
              <FlexContainer
                direction="column"
                alignItems="flex-start"
                fullWidth
              >
                <RuleBuilder
                  value={editableAudience.rule}
                  onChange={(nextRule) =>
                    nextRule &&
                    setEditableAudience((prev) => ({ ...prev, rule: nextRule }))
                  }
                  config={config}
                  variant="compact"
                  nullable
                />
                {errors.has('NO_RULE') && (
                  <FormHelperText error>
                    {t('Please provide at least one rule')}
                  </FormHelperText>
                )}
                <Button
                  onClick={() => previewDialog.openDialog()}
                  startIcon={<Eye size={16} />}
                  color="primary"
                  style={{ alignSelf: 'flex-end' }}
                >
                  {t('Preview matching employees')}
                </Button>
                {previewDialog.dialogOpen && (
                  <AudienceMatchDialog
                    orgId={value.data.orgId}
                    open={true}
                    onClose={previewDialog.closeDialog}
                    rule={editableAudience.rule}
                  />
                )}
              </FlexContainer>
            </CustomInputLabel>
          </>
        )}
        <FlexContainer justifyContent="flex-end" fullWidth>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={audience === editableAudience}
            onClick={() => {
              const nextErrors = new Set<'NO_RULE' | 'NO_NAME'>();
              if (editableAudience.rule !== 'EVERYONE') {
                if (!editableAudience.name) {
                  nextErrors.add('NO_NAME');
                }
                if (isEmptyRule(editableAudience.rule)) {
                  nextErrors.add('NO_RULE');
                }
              }
              setErrors(nextErrors);
              if (nextErrors.size) {
                return;
              }

              const i = originalAudiences.findIndex(
                (a) => a.id === editableAudience.id
              );
              const nextAudiences = [...originalAudiences];
              if (i === -1) {
                // for new audiences
                nextAudiences.unshift(editableAudience);

                trackEvent({
                  eventType: PAEventType.OTHER,
                  name: 'Moment Target Audience Added',
                  Explanation: 'Admin user added a target audience to a Moment',
                  'Moment ID': value.id,
                  'Moment Name': value.data.title,
                  'Target Audience Name': getAudienceName(
                    editableAudience,
                    nextAudiences.length > 1,
                    t
                  )
                });
              } else {
                nextAudiences[i] = editableAudience;
              }
              onChange({
                ...value,
                data: {
                  ...value.data,
                  content: { ...value.data.content, audiences: nextAudiences }
                }
              });
              onClose();
            }}
          >
            {t('Save changes')}
          </Button>
        </FlexContainer>
      </FlexContainer>
    </StandardDrawer>
  );
};

const DeletedAudiencesSectionContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

export const EditSectionAudience = ({
  value,
  onChange
}: EditComponentProps) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { trackEvent } = useProductAnalytics();
  const [
    drawerAudience,
    setDrawerAudience
  ] = useState<AudienceWithCompletionCriteria | null>(null);

  const [showDeletedAudiences, setShowDeletedAudiences] = useState(false);

  const { org } = useCurrentUser();
  const [users] = useUsersByOrgId(org.id);
  const { ROUTES } = useRoutes();

  const audiences = value.data.content.audiences;
  const hasOtherAudiences = audiences.length > 1;

  const [activeAudiences, inactiveAudiences] = partition(
    audiences,
    (a) => !a.inactive
  );

  const restoreAudience = (audience: AudienceWithCompletionCriteria) => {
    const newAudiences = audiences.map((a) =>
      a.id === audience.id
        ? {
            ...a,
            inactive: false
          }
        : a
    );

    onChange({
      ...value,
      data: {
        ...value.data,
        content: {
          ...value.data.content,
          audiences: newAudiences
        }
      }
    });
  };

  const removeAudience = (audience: AudienceWithCompletionCriteria) => {
    const newAudiences = audiences.map<AudienceWithCompletionCriteria>((a) =>
      a.id === audience.id
        ? {
            ...a,
            inactive: true
          }
        : a
    );

    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Moment Target Audience Removed',
      Explanation: 'Admin user removed a target audience from a Moment',
      'Moment ID': value.id,
      'Moment Name': value.data.title,
      'Target Audience Name': getAudienceName(
        audience,
        value.data.content.audiences.length > 1,
        t
      )
    });

    onChange({
      ...value,
      data: {
        ...value.data,
        content: {
          ...value.data.content,
          audiences: newAudiences
        }
      }
    });
  };

  const onDuplicateAudience = (audience: AudienceWithCompletionCriteria) => {
    const prevBlocks = value.data.content.blocks;
    const newBlocks = getBlocksForMomentAudience(audience, prevBlocks).map(
      (b) => ({
        ...cloneDeep(b),
        id: shortid()
      })
    );

    const newAudience: AudienceWithCompletionCriteria = {
      ...audience,
      inactive: false,
      id: shortid(),
      blocks: newBlocks.map((b) => b.id),
      name: '',
      color: getRandomAudienceColorExceptPreviousColor(theme, audience.color)
    };

    onChange({
      ...value,
      data: {
        ...value.data,
        content: {
          blocks: [...newBlocks, ...prevBlocks],
          audiences: [newAudience, ...audiences]
        }
      }
    });

    return newAudience;
  };

  if (isFreeTierOrg(org)) {
    if (!users) {
      return <Loader height={50} />;
    }
    if (users && !users.length) {
      return (
        <FlexContainer direction="column" alignItems="flex-start">
          <Typography>
            {t(
              'You haven’t yet added any employees to your account. Download the CSV template and'
            )}
            <br />
            {t('upload them to get started.')}
          </Typography>
          <Typography></Typography>
          <Link to={ROUTES.users.standalone.url()}>
            <Button
              startIcon={<Plus size={16} />}
              variant="contained"
              color="primary"
            >
              {t('Add employees')}
            </Button>
          </Link>
        </FlexContainer>
      );
    }
  }

  return (
    <>
      {/* TODO:
    Add org properties to EditComponentProps then check to display commented out bit if or has no users / if org is free tier
    onClick to redirect to add employees screen.
    Cards need a max width. Long text increases the width of card

      <FlexContainer direction="column" alignItems="flex-start">
        <Typography>
          {t(
            'You haven’t yet added any employees to your account. Download the CSV template and upload them to get started.'
          )}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => {}}>
          {t('Add employees')}
        </Button>
      </FlexContainer>

      */}
      <FlexContainer spacing={2}>
        <AddButton
          label={t('Add New Audience')}
          onClick={() => {
            const emptyAudience = getEmptyAudience(() =>
              getRandomAudienceColor(theme)
            );
            setDrawerAudience(emptyAudience);
          }}
        />
        {!audiences.some((a) => a.rule === 'EVERYONE') && (
          <AddButton
            label={
              audiences.length
                ? t('Add Everyone Else Audience')
                : t('Add Everyone Audience')
            }
            onClick={() => {
              const everyoneAudience = getEveryoneAudience();

              trackEvent({
                eventType: PAEventType.OTHER,
                name: 'Moment Target Audience Added',
                Explanation: 'Admin user added a target audience to a Moment',
                'Moment ID': value.id,
                'Moment Name': value.data.title,
                'Target Audience Name': getAudienceName(
                  everyoneAudience,
                  audiences.length > 0,
                  t
                )
              });

              onChange({
                ...value,
                data: {
                  ...value.data,
                  content: {
                    ...value.data.content,
                    audiences: [...audiences, everyoneAudience]
                  }
                }
              });
            }}
          />
        )}
      </FlexContainer>
      <AudiencesGrid>
        {sortBy(activeAudiences, (a) => a.rule === 'EVERYONE').map(
          (audience) => (
            <AudienceCard
              key={audience.id}
              audience={audience}
              hasOtherAudiences={hasOtherAudiences}
              onDeleteAudience={removeAudience}
              onRestoreAudience={restoreAudience}
              onDuplicateAudience={(audience) => {
                const duplicated = onDuplicateAudience(audience);
                setDrawerAudience(duplicated);
              }}
              setDrawerAudience={() => setDrawerAudience(audience)}
            />
          )
        )}
      </AudiencesGrid>
      {!!inactiveAudiences.length && (
        <DeletedAudiencesSectionContainer>
          {showDeletedAudiences ? (
            <>
              <Typography variant="h6">{t('Deleted audiences')}</Typography>
              <AudiencesGrid>
                {sortBy(inactiveAudiences, (a) => a.rule === 'EVERYONE').map(
                  (audience) => (
                    <AudienceCard
                      key={audience.id}
                      audience={audience}
                      hasOtherAudiences={hasOtherAudiences}
                      onDeleteAudience={removeAudience}
                      onRestoreAudience={restoreAudience}
                      onDuplicateAudience={(audience) => {
                        const duplicated = onDuplicateAudience(audience);
                        setDrawerAudience(duplicated);
                      }}
                      setDrawerAudience={() => setDrawerAudience(audience)}
                    />
                  )
                )}
              </AudiencesGrid>
            </>
          ) : (
            <DeletedAudiencesSectionContainer>
              <Button
                size="small"
                variant="text"
                color="primary"
                startIcon={<Archive size={16} />}
                onClick={() => setShowDeletedAudiences(true)}
              >
                {t('Show deleted audiences')}
              </Button>
            </DeletedAudiencesSectionContainer>
          )}
        </DeletedAudiencesSectionContainer>
      )}
      {drawerAudience && (
        <AudienceDrawer
          open={!!drawerAudience}
          onClose={() => setDrawerAudience(null)}
          value={value}
          onChange={onChange}
          audience={drawerAudience}
        />
      )}
    </>
  );
};

export const ViewSectionAudience = ({ value }: ViewComponentProps) => {
  const { t } = useUnsafeTranslation();
  const audiences = value.data.content.audiences.filter((a) => !a.inactive);
  const hasOtherAudiences = audiences.length > 1;

  return (
    <FlexContainer>
      {sortBy(audiences, (a) => a.rule === 'EVERYONE').map((audience) => (
        <AudienceBlockTitle>
          <WithShape
            wrap
            shape="square"
            color={audience.color || DEFAULT_AUDIENCE_COLOR}
          >
            <Typography variant="caption">
              {getAudienceName(audience, hasOtherAudiences, t)}
            </Typography>
          </WithShape>
        </AudienceBlockTitle>
      ))}
    </FlexContainer>
  );
};
